import { DetectionZone, Device, LaneLine, ScreenLine, Vector2, Vector3 } from "../models";
import { deviceToFacility, facilityToDevice } from "./geometryHelpers";
import { IntrinsicsTranslator } from "./point-translators/intrinsicTranslator";
import { withTiltFallback } from "../hooks/useImageCoordTranslator";

const TRANSLATOR_WIDTH = 640;
const TRANSLATOR_HEIGHT = 480;
const MAX_GROUND_RANGE_METERS = 200;

/**
 * A point must re-project back to within this many translator pixels of where it came from for
 * the round trip to be trusted. Points at or past the horizon get imageToGroundClamped's
 * synthetic max-range solution, which re-projects far from its source — that is the signal that
 * the point cannot be carried across a pose change and the shape must be left alone.
 */
const ROUNDTRIP_TOLERANCE_PX = TRANSLATOR_WIDTH * 0.015;

export type ReprojectableShape = DetectionZone | ScreenLine | LaneLine;

/** Clone a model instance with overrides, keeping its prototype so isComplete()/$type survive. */
export const clonePreservingType = <T,>(shape: T, overrides: object): T => Object.assign(Object.create(Object.getPrototypeOf(shape)), shape, overrides);

/** The subset of Device that decides how a pixel maps to the ground. */
const poseOf = (device: Device) => ({
    positionX: device.positionX ?? 0,
    positionY: device.positionY ?? 0,
    headingDegrees: device.headingDegrees ?? 0,
    height: device.height ?? 2,
    fusionOffsetPitch: withTiltFallback(device).fusionOffsetPitch ?? 0,
    fusionOffsetRoll: device.fusionOffsetRoll ?? 0,
    fusionEstimatedReturnHeight: device.fusionEstimatedReturnHeight ?? 0.8,
});

/** True when the two devices describe the same camera-to-ground mapping. */
export const posesEqual = (a: Device, b: Device): boolean => {
    const [pa, pb] = [poseOf(a), poseOf(b)];
    return (Object.keys(pa) as (keyof typeof pa)[]).every((k) => pa[k] === pb[k]);
};

/**
 * Only the pose terms the rigid rig transform does NOT already handle. Position and heading are
 * carried by translate/rotate in useDeviceAlignment, which always succeeds; these four require
 * re-deriving through the image and can fail near the horizon.
 */
export const cameraPoseChanged = (a: Device, b: Device): boolean => {
    const [pa, pb] = [poseOf(a), poseOf(b)];
    return pa.height !== pb.height || pa.fusionOffsetPitch !== pb.fusionOffsetPitch || pa.fusionOffsetRoll !== pb.fusionOffsetRoll || pa.fusionEstimatedReturnHeight !== pb.fusionEstimatedReturnHeight;
};

const hasHomography = (device: Device): boolean => (device.homographySourcePoints?.length ?? 0) >= 4 && (device.homographyDestinationPoints?.length ?? 0) >= 4;

const intrinsicsFor = (device: Device): IntrinsicsTranslator => {
    const t = new IntrinsicsTranslator();
    const withFallback = withTiltFallback(device);
    const radToDeg = 180 / Math.PI;
    const pitchDeg = (withFallback.fusionOffsetPitch ?? 0) * -radToDeg;
    const rollDeg = (withFallback.fusionOffsetRoll ?? 0) * radToDeg;
    t.calculateFromParameters(false, device.height ?? 2, pitchDeg, rollDeg, device.fusionEstimatedReturnHeight ?? 0.8);
    return t;
};

/**
 * Re-derive a camera-anchored shape's ground points after the device's camera pose changed.
 *
 * A shape drawn from the camera marks a feature in the photo. Correcting the device's height or
 * tilt does not move that feature, so the shape must keep covering the same pixels — which means
 * its stored ground coordinates have to be recomputed under the new pose. Without this the
 * coordinates stay frozen at a value derived from the old (wrong) pose and the shape slides off
 * whatever it was drawn on: a ~1.5 m height correction moves a 50 m point by ~30 m of ground
 * error, invisible on screen because the pixel shift stays small.
 *
 * Returns null when any point cannot survive the round trip (at/past the horizon, where
 * imageToGroundClamped returns a synthetic max-range point). Callers must leave such shapes
 * untouched and tell the user, rather than writing a corrupted geometry.
 */
export const reprojectShapePoints = (previous: Device, next: Device, points: Vector2[]): Vector2[] | null => {
    if (hasHomography(previous) || hasHomography(next)) return null;

    const from = intrinsicsFor(previous);
    const to = intrinsicsFor(next);
    const fromPos = { x: previous.positionX ?? 0, y: previous.positionY ?? 0 };
    const toPos = { x: next.positionX ?? 0, y: next.positionY ?? 0 };
    const fromHeading = previous.headingDegrees ?? 0;
    const toHeading = next.headingDegrees ?? 0;

    const result: Vector2[] = [];
    for (const point of points) {
        if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return null;

        const [devicePoint] = facilityToDevice([new Vector2({ x: point.x, y: point.y })], fromPos, fromHeading);
        const px = from.groundToImage(new Vector3({ x: devicePoint.x, y: devicePoint.y, z: 0 }));
        if (!Number.isFinite(px.x) || !Number.isFinite(px.y)) return null;

        const deviceGround = to.imageToGroundClamped(px, 0, MAX_GROUND_RANGE_METERS);
        if (!Number.isFinite(deviceGround.x) || !Number.isFinite(deviceGround.y)) return null;

        // Reject clamped/horizon solutions: a trustworthy point re-projects to where it came from.
        const back = to.groundToImage(new Vector3({ x: deviceGround.x, y: deviceGround.y, z: 0 }));
        if (!Number.isFinite(back.x) || !Number.isFinite(back.y)) return null;
        if (Math.hypot(back.x - px.x, back.y - px.y) > ROUNDTRIP_TOLERANCE_PX) return null;

        const [facilityPoint] = deviceToFacility([new Vector2({ x: deviceGround.x, y: deviceGround.y })], toPos, toHeading);
        result.push(new Vector2({ x: facilityPoint.x, y: facilityPoint.y }));
    }
    return result;
};

/**
 * Shapes that move with a device because they were drawn from its camera. Facility-scoped lane
 * lines count at single-device sites, where "the facility's lanes" and "this device's lanes" are
 * the same set — matching how the alignment rig has always selected them.
 */
export const selectAnchoredShapes = <T extends { $type?: string }>(allShapes: T[], forDevice: Device, isOnlyDevice: boolean): (T & ReprojectableShape)[] =>
    allShapes.filter((s) => {
        if (s.$type === LaneLine.$type) {
            const laneLine = s as unknown as LaneLine;
            return laneLine.deviceId === forDevice.id || (!laneLine.deviceId && isOnlyDevice);
        }
        if (s.$type === DetectionZone.$type || s.$type === ScreenLine.$type) {
            return (s as unknown as DetectionZone | ScreenLine).deviceId === forDevice.id;
        }
        return false;
    }) as (T & ReprojectableShape)[];

export { TRANSLATOR_HEIGHT, TRANSLATOR_WIDTH };
